import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentGroup, StudentGroupEditResource, StudentGroupViewResource } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import ApiService from "services/apiService";
import { StudentGroupPageBody } from "./StudentGroupPageBody/StudentGroupPageBody";
import { StudentGroupPageHeader } from "./StudentGroupPageHeader";
import { useNavigate } from "react-router-dom";

const routeName = "studentGroup";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const studentGroupPageRoute: PageRoute<Params> = {
  handle: "Student Group",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

const ROOT_PERMISSION: Permission = Permission.StudentGroup;

export const StudentGroupPage = () => {
  const { params } = useParams(routeName, Params);
  const navigate = useNavigate();
  const [group, setGroup] = useState<StudentGroup>({} as StudentGroup);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [group] });
  const [viewResource, setViewResource] = useState<StudentGroupViewResource>({});
  const [editResource, setEditResource] = useState<StudentGroupEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentGroupPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.studentGroups.get(id).then((response) => {
      setGroup(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentGroups.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentGroups.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentGroupPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentGroupPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      group={group}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentGroupPageBody
      crudMode={params.crudMode}
      group={group}
      setGroup={setGroup}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      fetch={fetch}
    />
  );

  return (
    <Page isForm header={pageHeader}>
      {pageBody}
    </Page>
  );
};
